/**
 * ============================================================================
 * RECIPES.JSX v25.5 - BUTTON STRATEGY COMPLIANCE
 * ============================================================================
 *
 * 🔧 v25.5: BUTTON STRATEGY COMPLIANCE (alle Inline-Styles korrigiert)
 *   FIX 1:  createBtn → FAB (56px, fixed bottom-right, gradient, draggable-ready)
 *   FIX 2:  shopBtn → L4 Icon-Button (#F8FAFC + border, KEIN Gradient)
 *   FIX 3:  copyBtn → L4 Icon-Button (#F8FAFC + border, KEIN Blau-Gradient)
 *   FIX 4:  deleteBtn → L4 Icon-Button mit Danger-Hover
 *   FIX 5:  viewToggle → 36px Chip-Höhe, pill (100px radius), overflow:hidden
 *   FIX 6:  toggleBtn → Keine innere Rundung, 0.82rem, 500 weight
 *   FIX 7:  Modal-Footer Buttons → L1 Primary (52px) + L2 Secondary (44px)
 *   FIX 8:  List-Buttons → L4 Icon-Buttons (44px touch, konsistent)
 *   FIX 9:  retryBtn → L1 Primary (52px, 14px radius)
 *   FIX 10: Modal Close → L4 Mini (32px + 44px touch)
 *   KEPT:   infoBtn bleibt unverändert (per User-Anweisung)
 *
 * 🔧 v25.4: MOBILE RESPONSIVE - Info Button 36px auf Mobile
 * 🔧 v25.3: INFO BUTTON - Info Modal für Modul-Beschreibung
 * 🔧 v25.2: SEARCH FIX - Verbesserte Suchfunktion
 * 🔧 v25.1: LIST VIEW FIX - Deutlicher Unterschied zwischen Karten und Liste
 * 🔧 v25.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 * 🔧 v24.2: DUAL ENDPOINT FIX
 * ✅ v24.0: ANALYTICS TRACKING
 * ✅ v23.4: HYBRID BILD-SYSTEM
 * ✅ v23.0: Planivio Rebranding
 *
 * @version 25.5.0 - Button Strategy Compliance
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext';
import { useTranslation } from '../../context/I18nContext';
import Loading, { 
  LoadingIndicator, 
  Skeleton, 
  SkeletonCard, 
  SkeletonGrid,
  EmptyState 
} from '../common/Loading';
import CreateRecipeModal from './CreateRecipeModal';
import AddToShoppingModal from './AddToShoppingModal';
import { getRecipeEmoji } from './ImageSelector';
import './Recipes.css';

// ============================================================================
// ✅ v24: ANALYTICS + API CONFIG
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;

// PLANIVIO FARBEN
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  warm: '#E8976C',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#F0F9FF',
  bgPage: '#F0F9FF',
  text: '#1F2937',
  textMuted: '#6B7280',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  gradientHover: 'linear-gradient(135deg, #3D7A8C 0%, #5A9680 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)',
  shadowHover: 'rgba(74, 144, 164, 0.4)'
};

// ✅ v25.5: BUTTON STRATEGY DESIGN TOKENS
const BTN = {
  // L1 Primary
  primaryH: '52px',
  primaryR: '14px',
  primaryPad: '0 28px',
  primaryFs: '0.95rem',
  primaryShadow: '0 4px 16px rgba(74,144,164,0.25)',
  // L2 Secondary
  secondaryH: '44px',
  secondaryR: '12px',
  secondaryPad: '0 20px',
  secondaryFs: '0.9rem',
  // L4 Icon
  iconSize: '44px',
  iconR: '12px',
  iconBg: '#F8FAFC',
  iconBorder: '1px solid #E2E8F0',
  iconColor: '#475569',
  // L4 Mini
  iconMiniSize: '32px',
  iconMiniR: '8px',
  // L5 FAB
  fabSize: '56px',
  fabR: '16px',
  fabShadow: '0 6px 24px rgba(74,144,164,0.35)',
  // Chip
  chipH: '36px',
  chipR: '100px',
  chipFs: '0.82rem',
  // Danger
  dangerColor: '#EF4444',
  dangerBg: '#FEE2E2',
  // Common
  fw: 600,
  font: "'DM Sans', 'Inter', sans-serif",
  gap: '12px',
  gapCompact: '8px',
};

console.log('🍳 Recipes.jsx v25.5 loaded - BUTTON STRATEGY COMPLIANCE 🎯');

/**
 * ✅ v24: Safe Track Function
 */
const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Recipes').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ============================================================================
// GLOBALER STATE + SHARED PROMISE
// ============================================================================
let GLOBAL_MY_RECIPES_CACHE = [];
let GLOBAL_COMMUNITY_RECIPES_CACHE = [];
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ============================================================================
// COMPONENT
// ============================================================================
const Recipes = () => {
  
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { token, loading: tokenLoading } = useToken();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const isDark = false;
  const styles = useMemo(() => getStyles(isDark, isMobile), [isDark, isMobile]);

  // STATE
  const [myRecipes, setMyRecipes] = useState(() => GLOBAL_MY_RECIPES_CACHE);
  const [communityRecipes, setCommunityRecipes] = useState(() => GLOBAL_COMMUNITY_RECIPES_CACHE);
  const [loading, setLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeToCopy, setRecipeToCopy] = useState(null);
  const [shoppingModalRecipe, setShoppingModalRecipe] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // REFS
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasTrackedViewRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // ============================================================================
  // ✅ v25.4: Mobile Responsive Listener
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================================================
  // LIFECYCLE
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Recipes MOUNTED');

    return () => {
      isMountedRef.current = false;
      console.log('🔄 Recipes UNMOUNTING');
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (token && !hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      console.log('🔐 Recipes: Using token from TokenContext');
      trackAnalyticsEvent(token, 'component_view', { 
        component: 'Recipes',
        myRecipesCount: GLOBAL_MY_RECIPES_CACHE.length,
        communityRecipesCount: GLOBAL_COMMUNITY_RECIPES_CACHE.length
      });
    }
  }, [token]);

  // ============================================================================
  // ANALYTICS HELPER
  // ============================================================================
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) {
      console.warn('❌ No token available for analytics');
      return;
    }
    
    trackAnalyticsEvent(token, eventName, {
      component: 'Recipes',
      ...metadata
    });
  }, [token]);

  useEffect(() => {
    if (myRecipes.length > 0 || communityRecipes.length > 0) {
      GLOBAL_MY_RECIPES_CACHE = myRecipes;
      GLOBAL_COMMUNITY_RECIPES_CACHE = communityRecipes;
    }
  }, [myRecipes, communityRecipes]);

  const allRecipes = useMemo(() => {
    const myMarked = myRecipes.map(r => ({ ...r, isCommunity: false }));
    const communityMarked = communityRecipes.map(r => ({ ...r, isCommunity: true }));
    
    if (sourceFilter === 'my') return myMarked;
    if (sourceFilter === 'community') return communityMarked;
    return [...myMarked, ...communityMarked];
  }, [myRecipes, communityRecipes, sourceFilter]);

  // ============================================================================
  // FETCH
  // ============================================================================
  const fetchRecipesFromBackend = useCallback(async () => {
    if (!token) throw new Error('Kein Token verfügbar');
    
    const headers = { 
      'Authorization': `Bearer ${token}`, 
      'Content-Type': 'application/json' 
    };
    
    console.log('🍳 Fetching from BOTH endpoints...');
    
    const [myResponse, communityResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/api/recipes`, { headers }),
      fetch(`${API_BASE_URL}/api/recipes/community`, { headers })
    ]);
    
    let myRecipesList = [];
    if (myResponse.ok) {
      const myData = await myResponse.json();
      myRecipesList = Array.isArray(myData) ? myData : (myData.recipes || []);
      console.log('🍳 My recipes loaded:', myRecipesList.length);
    } else {
      console.warn('⚠️ My recipes failed:', myResponse.status);
    }
    
    let communityRecipesList = [];
    if (communityResponse.ok) {
      const communityData = await communityResponse.json();
      communityRecipesList = Array.isArray(communityData) ? communityData : (communityData.recipes || []);
      communityRecipesList = communityRecipesList.map(r => ({ ...r, isCommunity: true }));
      console.log('🌍 Community recipes loaded:', communityRecipesList.length);
    } else {
      console.warn('⚠️ Community recipes failed:', communityResponse.status);
    }
    
    return {
      myRecipes: myRecipesList,
      communityRecipes: communityRecipesList
    };
  }, [token]);

  const loadRecipes = useCallback(async (forceRefresh = false) => {
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    if (GLOBAL_HAS_LOADED && (GLOBAL_MY_RECIPES_CACHE.length > 0 || GLOBAL_COMMUNITY_RECIPES_CACHE.length > 0) && !forceRefresh) {
      if (isMountedRef.current) {
        setMyRecipes(GLOBAL_MY_RECIPES_CACHE);
        setCommunityRecipes(GLOBAL_COMMUNITY_RECIPES_CACHE);
        setLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }
    
    if (GLOBAL_LOADING_PROMISE) {
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        if (isMountedRef.current) {
          setMyRecipes(result.myRecipes);
          setCommunityRecipes(result.communityRecipes);
          setLoading(false);
          setIsInitialLoad(false);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err.message);
          setLoading(false);
          setIsInitialLoad(false);
        }
      }
      return;
    }
    
    if (isMountedRef.current) {
      setLoading(true);
      setError(null);
      if (!isInitialLoad) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setShowLoading(true);
        }, LOADING_DELAY_MS);
      }
    }
    
    GLOBAL_LOADING_PROMISE = fetchRecipesFromBackend();
    
    try {
      const result = await GLOBAL_LOADING_PROMISE;
      GLOBAL_MY_RECIPES_CACHE = result.myRecipes;
      GLOBAL_COMMUNITY_RECIPES_CACHE = result.communityRecipes;
      GLOBAL_HAS_LOADED = true;
      
      if (isMountedRef.current) {
        setMyRecipes(result.myRecipes);
        setCommunityRecipes(result.communityRecipes);
        setIsInitialLoad(false);
      }
    } catch (err) {
      trackEvent('recipes_load_error', { error: err.message });
      
      if (isMountedRef.current) {
        setError(err.message);
        setIsInitialLoad(false);
        if (GLOBAL_MY_RECIPES_CACHE.length > 0 || GLOBAL_COMMUNITY_RECIPES_CACHE.length > 0) {
          setMyRecipes(GLOBAL_MY_RECIPES_CACHE);
          setCommunityRecipes(GLOBAL_COMMUNITY_RECIPES_CACHE);
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      if (isMountedRef.current) { setLoading(false); setShowLoading(false); }
      if (loadingTimerRef.current) { clearTimeout(loadingTimerRef.current); loadingTimerRef.current = null; }
    }
  }, [isInitialLoad, fetchRecipesFromBackend, trackEvent, token]);

  // Initial Load
  useEffect(() => {
    if (GLOBAL_HAS_LOADED && (GLOBAL_MY_RECIPES_CACHE.length > 0 || GLOBAL_COMMUNITY_RECIPES_CACHE.length > 0)) {
      setMyRecipes(GLOBAL_MY_RECIPES_CACHE);
      setCommunityRecipes(GLOBAL_COMMUNITY_RECIPES_CACHE);
      setLoading(false);
      setIsInitialLoad(false);
      return;
    }
    if (isAuthLoading || tokenLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      setIsInitialLoad(false);
      setError(t('recipes.notLoggedIn'));
      return;
    }
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    loadRecipes();
  }, [isAuthenticated, isAuthLoading, tokenLoading, token, loadRecipes, t]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleCreateRecipe = useCallback(async (recipeData) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticRecipe = { ...recipeData, id: tempId, visibility: 'private', created_at: new Date().toISOString(), _isTemp: true };
    
    setMyRecipes(prev => [optimisticRecipe, ...prev]);
    GLOBAL_MY_RECIPES_CACHE = [optimisticRecipe, ...GLOBAL_MY_RECIPES_CACHE];
    setShowCreateModal(false);
    setRecipeToCopy(null);
    
    trackEvent('recipes_create', { 
      category: recipeData.category,
      difficulty: recipeData.difficulty,
      ingredients_count: recipeData.ingredients?.length || 0,
      is_copy: !!recipeToCopy
    });
    
    try {
      if (!token) throw new Error('Kein Token verfügbar');
      
      const response = await fetch(`${API_BASE_URL}/api/recipes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(recipeData)
      });
      
      if (!response.ok) throw new Error(t('recipes.errorCreating'));
      
      const result = await response.json();
      const newRecipe = result.recipe || result;
      
      setMyRecipes(prev => prev.map(r => r.id === tempId ? { ...newRecipe, _isTemp: false } : r));
      GLOBAL_MY_RECIPES_CACHE = GLOBAL_MY_RECIPES_CACHE.map(r => r.id === tempId ? { ...newRecipe, _isTemp: false } : r);
    } catch (err) {
      trackEvent('recipes_create_error', { error: err.message });
      setMyRecipes(prev => prev.filter(r => r.id !== tempId));
      GLOBAL_MY_RECIPES_CACHE = GLOBAL_MY_RECIPES_CACHE.filter(r => r.id !== tempId);
      alert(t('common.error') + ': ' + err.message);
    }
  }, [t, recipeToCopy, trackEvent, token]);

  const handleCopyRecipe = useCallback((recipe, e) => {
    if (e) e.stopPropagation();
    
    trackEvent('recipes_copy', { category: recipe.category, from_community: true });
    
    setRecipeToCopy({
      name: recipe.name + ' (Kopie)',
      description: recipe.description,
      prep_time: recipe.prep_time || recipe.prepTime,
      cook_time: recipe.cook_time || recipe.cookTime || 0,
      servings: recipe.servings,
      difficulty: recipe.difficulty,
      category: recipe.category,
      ingredients: (recipe.ingredients || []).map(ing => ({ name: ing.name || '', quantity: ing.quantity || '', unit: ing.unit || '', category: ing.category || 'Sonstiges' })),
      instructions: (recipe.instructions || []).map(inst => typeof inst === 'string' ? inst : inst.instruction || ''),
      image_url: recipe.image_url || recipe.imageUrl || null,
      image_key: recipe.image_key || null
    });
    setShowCreateModal(true);
  }, [trackEvent]);

  const handleDeleteRecipe = useCallback(async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(t('recipes.confirmDelete'))) return;
    
    const deletedRecipe = myRecipes.find(r => r.id === id);
    const deletedIndex = myRecipes.findIndex(r => r.id === id);
    
    setMyRecipes(prev => prev.filter(r => r.id !== id));
    GLOBAL_MY_RECIPES_CACHE = GLOBAL_MY_RECIPES_CACHE.filter(r => r.id !== id);
    
    trackEvent('recipes_delete', { recipe_id: id, category: deletedRecipe?.category });
    
    try {
      if (!token) throw new Error('Kein Token verfügbar');
      
      const response = await fetch(`${API_BASE_URL}/api/recipes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Delete failed');
    } catch (err) {
      trackEvent('recipes_delete_error', { error: err.message });
      if (deletedRecipe) {
        setMyRecipes(prev => { const newList = [...prev]; newList.splice(deletedIndex, 0, deletedRecipe); return newList; });
        GLOBAL_MY_RECIPES_CACHE.splice(deletedIndex, 0, deletedRecipe);
      }
      alert(t('recipes.errorDeleting'));
    }
  }, [myRecipes, t, trackEvent, token]);

  const handleToShopping = useCallback((recipe, e) => {
    if (e) e.stopPropagation();
    trackEvent('recipes_to_shopping', { recipe_id: recipe.id, category: recipe.category, ingredients_count: recipe.ingredients?.length || 0, is_community: recipe.isCommunity });
    setShoppingModalRecipe(recipe);
  }, [trackEvent]);

  const handleShoppingSuccess = useCallback((result) => {
    alert(result.isNewList
      ? `✅ Neue Einkaufsliste "${result.list.name}" mit ${result.itemCount} Artikeln erstellt!`
      : `✅ ${result.itemCount} Artikel zu "${result.list.name}" hinzugefügt!`);
    
    if (result.copiedRecipe) {
      setMyRecipes(prev => [result.copiedRecipe, ...prev]);
      GLOBAL_MY_RECIPES_CACHE = [result.copiedRecipe, ...GLOBAL_MY_RECIPES_CACHE];
    }
  }, []);

  const handleOpenRecipeDetail = useCallback((recipe) => {
    trackEvent('recipes_detail_view', { recipe_id: recipe.id, category: recipe.category, is_community: recipe.isCommunity });
    setSelectedRecipe(recipe);
  }, [trackEvent]);

  const handleRetry = useCallback(() => {
    trackEvent('recipes_retry', { type: 'manual' });
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    setError(null);
    setIsInitialLoad(true);
    loadRecipes(true);
  }, [loadRecipes, trackEvent]);

  const handleSilentRefresh = useCallback(() => {
    trackEvent('recipes_refresh', { type: 'silent' });
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    loadRecipes(true);
  }, [loadRecipes, trackEvent]);

  // ============================================================================
  // FILTER HANDLERS
  // ============================================================================
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        trackEvent('recipes_search', { query_length: value.length });
      }, 500);
    }
  }, [trackEvent]);

  const handleSourceFilterChange = useCallback((e) => {
    const value = e.target.value;
    setSourceFilter(value);
    trackEvent('recipes_filter_source', { source: value });
  }, [trackEvent]);

  const handleCategoryFilterChange = useCallback((e) => {
    const value = e.target.value;
    setCategoryFilter(value);
    trackEvent('recipes_filter_category', { category: value });
  }, [trackEvent]);

  const handleDifficultyFilterChange = useCallback((e) => {
    const value = e.target.value;
    setDifficultyFilter(value);
    trackEvent('recipes_filter_difficulty', { difficulty: value });
  }, [trackEvent]);

  const handleViewModeChange = useCallback((newMode) => {
    trackEvent('recipes_view_mode_change', { from_mode: viewMode, to_mode: newMode });
    setViewMode(newMode);
  }, [viewMode, trackEvent]);

  // FILTERING
  const filteredRecipes = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();

    return allRecipes.filter(recipe => {
      let matchesSearch = true;
      if (searchLower) {
        const nameMatch = recipe.name?.toLowerCase().includes(searchLower) || false;
        const descMatch = recipe.description?.toLowerCase().includes(searchLower) || false;
        const catMatch = recipe.category?.toLowerCase().includes(searchLower) || false;
        const ingredientMatch = recipe.ingredients?.some(ing =>
          ing.name?.toLowerCase().includes(searchLower)
        ) || false;
        matchesSearch = nameMatch || descMatch || catMatch || ingredientMatch;
      }
      const matchesCategory = categoryFilter === 'all' || recipe.category === categoryFilter;
      const matchesDifficulty = difficultyFilter === 'all' || recipe.difficulty === difficultyFilter;
      return matchesSearch && matchesCategory && matchesDifficulty;
    });
  }, [allRecipes, searchTerm, categoryFilter, difficultyFilter]);

  const stats = useMemo(() => ({
    total: myRecipes.length + communityRecipes.length,
    myRecipes: myRecipes.length,
    community: communityRecipes.length
  }), [myRecipes.length, communityRecipes.length]);

  const uniqueCategories = useMemo(() => {
    const cats = new Set();
    allRecipes.forEach(r => { if (r.category) cats.add(r.category); });
    return Array.from(cats).sort();
  }, [allRecipes]);

  // HELPERS
  const getDifficultyStyle = (difficulty) => {
    const colors = {
      'Leicht': { bg: '#D1FAE5', color: '#059669', dot: '🟢' },
      'Easy': { bg: '#D1FAE5', color: '#059669', dot: '🟢' },
      'Mittel': { bg: '#FEF3C7', color: '#D97706', dot: '🟡' },
      'Medium': { bg: '#FEF3C7', color: '#D97706', dot: '🟡' },
      'Schwer': { bg: '#FEE2E2', color: '#DC2626', dot: '🔴' },
      'Hard': { bg: '#FEE2E2', color: '#DC2626', dot: '🔴' }
    };
    return colors[difficulty] || colors['Mittel'];
  };

  const getRecipeTags = (recipe) => {
    const tags = [];
    if (recipe.isQuick) tags.push({ key: 'quick', label: '⚡ Schnell', color: '#D1FAE5', textColor: '#065F46' });
    if (recipe.isBudget) tags.push({ key: 'budget', label: '💰 Budget', color: '#D1FAE5', textColor: '#065F46' });
    if (recipe.isFamily) tags.push({ key: 'family', label: '👨‍👩‍👧‍👦 Familie', color: '#EDE9FE', textColor: '#5B21B6' });
    return tags;
  };

  // ============================================================================
  // RENDER: SKELETON
  // ============================================================================
  if ((isInitialLoad || tokenLoading) && myRecipes.length === 0 && communityRecipes.length === 0 && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <Skeleton width={48} height={48} borderRadius={12} />
            <div>
              <Skeleton width={120} height={24} style={{ marginBottom: 8 }} />
              <Skeleton width={150} height={16} />
            </div>
          </div>
        </div>
        <Skeleton width={180} height={36} borderRadius={100} style={{ marginBottom: '1rem' }} />
        <div style={{ ...styles.filtersContainer, padding: '1rem' }}>
          <Skeleton width={200} height={40} borderRadius={10} />
          <Skeleton width={140} height={40} borderRadius={10} />
          <Skeleton width={140} height={40} borderRadius={10} />
          <Skeleton width={160} height={40} borderRadius={10} />
        </div>
        <SkeletonGrid count={6} cardHeight={280} />
      </div>
    );
  }

  if (isAuthLoading) {
    return <div style={styles.container}><Loading icon="🔐" text="Authentifizierung..." /></div>;
  }

  if (error && myRecipes.length === 0 && communityRecipes.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={{ fontSize: '3rem' }}>⚠️</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
          {/* 🔧 FIX 9: retryBtn → L1 Primary (52px, 14px) */}
          <button style={styles.retryBtn} onClick={handleRetry}>🔄 {t('common.retry')}</button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER RECIPE CARD — 🔧 v25.5: Card-Actions als L4 Icon-Buttons
  // ============================================================================
  const renderRecipeCard = (recipe) => {
    const prepTime = recipe.prep_time || recipe.prepTime || 0;
    const cookTime = recipe.cook_time || recipe.cookTime || 0;
    const totalTime = prepTime + cookTime;
    const ingredientCount = recipe.ingredients?.length || 0;
    const diffStyle = getDifficultyStyle(recipe.difficulty);
    const isCommunity = recipe.isCommunity;
    const tags = getRecipeTags(recipe);
    
    const imageUrl = recipe.image_url || recipe.imageUrl || '';
    const imageKey = recipe.image_key || '';
    const isRealImage = imageUrl.startsWith('http') || imageUrl.startsWith('/');
    const emoji = getRecipeEmoji(imageKey || imageUrl);
    
    return (
      <div key={`${isCommunity ? 'c' : 'm'}-${recipe.id}`} style={styles.card} onClick={() => handleOpenRecipeDetail(recipe)}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 8px 25px ${PLANIVIO.shadow}`; e.currentTarget.style.borderColor = PLANIVIO.teal; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = PLANIVIO.border; }}>
        
        <div style={styles.cardImage}>
          {isRealImage ? (
            <img 
              src={imageUrl} 
              alt={recipe.name} 
              style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }} 
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
            />
          ) : null}
          <span style={{ ...styles.cardEmoji, display: isRealImage ? 'none' : 'block' }}>{emoji}</span>
          <span style={styles.categoryBadge}>{recipe.category}</span>
          {isCommunity && <span style={styles.communityBadge}>🌍 Community</span>}
        </div>
        
        <div style={styles.cardContent}>
          <h3 style={styles.cardTitle}>{recipe.name}</h3>
          <p style={styles.cardDescription}>{recipe.description}</p>
          <div style={styles.cardMeta}>
            <span style={styles.metaBadge}>⏱️ {totalTime} Min</span>
            <span style={styles.metaBadge}>👥 {recipe.servings} Port.</span>
            <span style={styles.metaBadge}>📋 {ingredientCount} Zutaten</span>
          </div>
          {tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              {tags.map(tag => (<span key={tag.key} style={{ padding: '0.25rem 0.5rem', background: tag.color, color: tag.textColor, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600 }}>{tag.label}</span>))}
            </div>
          )}
          <span style={{ ...styles.difficultyBadge, background: diffStyle.bg, color: diffStyle.color }}>{diffStyle.dot} {recipe.difficulty}</span>
          
          {/* 🔧 FIX 2+3+4: Card-Actions als L4 Icon-Buttons */}
          <div style={styles.cardActions}>
            <button 
              style={styles.shopBtn} 
              onClick={(e) => handleToShopping(recipe, e)} 
              title="Zur Einkaufsliste"
              onMouseEnter={(e) => { e.currentTarget.style.background = PLANIVIO.bgLight; e.currentTarget.style.borderColor = PLANIVIO.teal; e.currentTarget.style.color = PLANIVIO.teal; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = BTN.iconColor; }}
            >🛒</button>
            {isCommunity ? (
              <button 
                style={styles.copyBtn} 
                onClick={(e) => handleCopyRecipe(recipe, e)} 
                title="In meine Rezepte kopieren"
                onMouseEnter={(e) => { e.currentTarget.style.background = PLANIVIO.bgLight; e.currentTarget.style.borderColor = PLANIVIO.teal; e.currentTarget.style.color = PLANIVIO.teal; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = BTN.iconColor; }}
              >📋</button>
            ) : (
              <button 
                style={styles.deleteBtn} 
                onClick={(e) => handleDeleteRecipe(recipe.id, e)} 
                title={t('common.delete')}
                onMouseEnter={(e) => { e.currentTarget.style.background = BTN.dangerBg; e.currentTarget.style.borderColor = '#FECACA'; e.currentTarget.style.color = BTN.dangerColor; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = PLANIVIO.textMuted; }}
              >🗑️</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER LIST ITEM — 🔧 v25.5: List-Buttons als L4 Icon-Buttons
  // ============================================================================
  const renderListItem = (recipe) => {
    const prepTime = recipe.prep_time || recipe.prepTime || 0;
    const cookTime = recipe.cook_time || recipe.cookTime || 0;
    const totalTime = prepTime + cookTime;
    const ingredientCount = recipe.ingredients?.length || 0;
    const diffStyle = getDifficultyStyle(recipe.difficulty);
    const isCommunity = recipe.isCommunity;
    
    const imageUrl = recipe.image_url || recipe.imageUrl || '';
    const imageKey = recipe.image_key || '';
    const isRealImage = imageUrl.startsWith('http') || imageUrl.startsWith('/');
    const emoji = getRecipeEmoji(imageKey || imageUrl);
    
    return (
      <div key={`${isCommunity ? 'c' : 'm'}-${recipe.id}`} style={styles.listItem} onClick={() => handleOpenRecipeDetail(recipe)}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = PLANIVIO.teal; e.currentTarget.style.transform = 'translateX(4px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = PLANIVIO.border; e.currentTarget.style.transform = 'translateX(0)'; }}>
        <div style={{ ...styles.listImage, overflow: 'hidden', position: 'relative' }}>
          {isRealImage ? (
            <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0, borderRadius: '8px' }} onError={(e) => { e.target.style.display = 'none'; }} />
          ) : null}
          <span style={{ display: isRealImage ? 'none' : 'block' }}>{emoji}</span>
        </div>

        <div style={styles.listContent}>
          <h3 style={styles.listTitle}>
            {recipe.name || 'Unbenanntes Rezept'}
            {isCommunity && <span style={styles.listCommunityBadge}>Community</span>}
          </h3>
          <div style={styles.listMeta}>
            <span>⏱️ {totalTime} Min</span>
            <span>👥 {recipe.servings || 0} Port.</span>
            <span>📋 {ingredientCount} Zutaten</span>
          </div>
        </div>

        <div style={styles.listBadges}>
          <span style={styles.listCategoryBadge}>{recipe.category || 'Sonstige'}</span>
          <span style={{ ...styles.listDiffBadge, background: diffStyle.bg, color: diffStyle.color }}>{recipe.difficulty || 'Mittel'}</span>
        </div>

        {/* 🔧 FIX 8: List-Buttons als L4 Icon-Buttons (44px touch) */}
        <div style={{ display: 'flex', gap: BTN.gapCompact, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button 
            style={styles.listShopBtn} 
            onClick={(e) => handleToShopping(recipe, e)} 
            title="Zutaten zur Einkaufsliste"
            onMouseEnter={(e) => { e.currentTarget.style.background = PLANIVIO.bgLight; e.currentTarget.style.borderColor = PLANIVIO.teal; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; }}
          >🛒</button>
          {isCommunity ? (
            <button 
              style={styles.listCopyBtn} 
              onClick={(e) => handleCopyRecipe(recipe, e)} 
              title="Rezept kopieren"
              onMouseEnter={(e) => { e.currentTarget.style.background = PLANIVIO.bgLight; e.currentTarget.style.borderColor = PLANIVIO.teal; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; }}
            >📋</button>
          ) : (
            <button 
              style={styles.listDeleteBtn} 
              onClick={(e) => handleDeleteRecipe(recipe.id, e)} 
              title="Löschen"
              onMouseEnter={(e) => { e.currentTarget.style.background = BTN.dangerBg; e.currentTarget.style.borderColor = '#FECACA'; e.currentTarget.style.color = BTN.dangerColor; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = BTN.iconBg; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = PLANIVIO.textMuted; }}
            >🗑️</button>
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: MAIN
  // ============================================================================
  return (
    <div style={styles.container}>
      {showLoading && <LoadingIndicator text="Aktualisiere..." position="fixed" />}
      
      {error && (myRecipes.length > 0 || communityRecipes.length > 0) && (
        <div style={styles.errorBanner}><span>⚠️ {error}</span><button onClick={() => setError(null)} style={styles.errorCloseBtn}>✕</button></div>
      )}
      
      <div style={{ opacity: showLoading ? 0.7 : 1, transition: 'opacity 0.2s ease', pointerEvents: showLoading ? 'none' : 'auto' }}>
        
        {/* HEADER - Row 1: Icon + Title + Info + Hamburger */}
        <div className="recipes-header-row1">
          <div className="recipes-header-left">
            <div className="recipes-header-icon">🍳</div>
            <div className="recipes-header-title-wrap">
              <h1 className="recipes-header-title">{t('recipes.title')}</h1>
              <span className="recipes-header-subtitle">
                {myRecipes.length + communityRecipes.length} Rezepte
              </span>
            </div>
            <button
              className="recipes-info-btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Modul-Info"
            >
              ⓘ
            </button>
          </div>

        </div>

        {/* Info Modal */}
        {showInfo && (
          <div style={styles.infoModal}>
            <div style={styles.infoModalHeader}>
              <span style={styles.infoModalTitle}>🍳 Rezepte - Info</span>
              <button style={styles.infoCloseBtn} onClick={() => setShowInfo(false)}>✕</button>
            </div>
            <div style={styles.infoModalContent}>
              <p style={styles.infoText}>
                <strong>Rezepte-Modul</strong> ermöglicht dir das Verwalten deiner Lieblingsrezepte.
                Erstelle eigene Rezepte, entdecke Community-Rezepte und füge Zutaten direkt
                zur Einkaufsliste hinzu.
              </p>
              <ul style={styles.infoList}>
                <li>Erstelle und speichere eigene Rezepte</li>
                <li>Entdecke Rezepte aus der Community</li>
                <li>Kopiere Community-Rezepte in deine Sammlung</li>
                <li>Füge Zutaten mit einem Klick zur Einkaufsliste hinzu</li>
                <li>Filtere nach Kategorie und Schwierigkeitsgrad</li>
                <li>Suche nach Rezeptnamen oder Zutaten</li>
              </ul>
            </div>
          </div>
        )}

        {/* 🔧 FIX 5+6: Row 2: View Toggle ONLY (kein createBtn hier — wird FAB!) */}
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={styles.viewToggle}>
            <button 
              style={{ ...styles.toggleBtn, ...(viewMode === 'grid' ? styles.toggleBtnActive : styles.toggleBtnInactive) }} 
              onClick={() => handleViewModeChange('grid')}
            >▦ Karten</button>
            <button 
              style={{ ...styles.toggleBtn, ...(viewMode === 'list' ? styles.toggleBtnActive : styles.toggleBtnInactive) }} 
              onClick={() => handleViewModeChange('list')}
            >☰ Liste</button>
          </div>
        </div>
        
        {/* FILTERS */}
        <div style={styles.filtersContainer}>
          <input 
            type="text" 
            placeholder="🔍 Suchen..." 
            value={searchTerm} 
            onChange={handleSearchChange} 
            style={styles.searchInput} 
          />
          <select value={sourceFilter} onChange={handleSourceFilterChange} style={styles.selectInput}>
            <option value="all">📚 Alle Quellen</option>
            <option value="my">🍳 Meine Rezepte</option>
            <option value="community">🌍 Community</option>
          </select>
          <select value={categoryFilter} onChange={handleCategoryFilterChange} style={styles.selectInput}>
            <option value="all">📁 Alle Kategorien</option>
            {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <select value={difficultyFilter} onChange={handleDifficultyFilterChange} style={styles.selectInput}>
            <option value="all">📊 Alle</option>
            <option value="Leicht">🟢 Leicht</option>
            <option value="Mittel">🟡 Mittel</option>
            <option value="Schwer">🔴 Schwer</option>
          </select>
        </div>
        
        {/* CONTENT */}
        {filteredRecipes.length === 0 ? (
          <EmptyState
            icon="🍳"
            title={searchTerm || categoryFilter !== 'all' || difficultyFilter !== 'all' ? 'Keine Rezepte gefunden' : sourceFilter === 'my' ? 'Noch keine eigenen Rezepte' : 'Keine Community Rezepte verfügbar'}
            description={searchTerm || categoryFilter !== 'all' || difficultyFilter !== 'all' ? 'Versuche andere Suchbegriffe oder Filter' : sourceFilter === 'my' ? 'Erstelle dein erstes Rezept oder kopiere eines aus der Community' : ''}
            actionText={sourceFilter === 'my' && myRecipes.length === 0 ? '🌍 Community durchstöbern' : null}
            onAction={sourceFilter === 'my' && myRecipes.length === 0 ? () => { setSourceFilter('community'); trackEvent('recipes_filter_source', { source: 'community', from_empty_state: true }); } : null}
          />
        ) : viewMode === 'grid' ? (
          <div style={styles.grid}>{filteredRecipes.map(renderRecipeCard)}</div>
        ) : (
          <div style={styles.listContainer}>{filteredRecipes.map(renderListItem)}</div>
        )}
      </div>
      
      {/* 🔧 FIX 1: FAB — Fixed bottom-right, 56px, gradient, draggable-ready */}
      <button 
        style={styles.fab}
        onClick={() => { setRecipeToCopy(null); setShowCreateModal(true); trackEvent('recipes_modal_open', { mode: 'create' }); }}
        title={t('recipes.createRecipe')}
      >
        ＋
      </button>
      
      {/* MODALS */}
      {showCreateModal && <CreateRecipeModal onClose={() => { setShowCreateModal(false); setRecipeToCopy(null); }} onCreate={handleCreateRecipe} initialData={recipeToCopy} t={t} isDark={isDark} />}
      {selectedRecipe && <RecipeDetailModal recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} onToShopping={handleToShopping} onCopyRecipe={handleCopyRecipe} t={t} isDark={isDark} />}
      {shoppingModalRecipe && <AddToShoppingModal recipe={shoppingModalRecipe} onClose={() => setShoppingModalRecipe(null)} onSuccess={handleShoppingSuccess} token={token} t={t} onRecipeCopied={(newRecipe) => { setMyRecipes(prev => [newRecipe, ...prev]); GLOBAL_MY_RECIPES_CACHE = [newRecipe, ...GLOBAL_MY_RECIPES_CACHE]; }} />}
      
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
};

// ============================================================================
// RECIPE DETAIL MODAL — 🔧 v25.5: FIX 7 + FIX 10
// ============================================================================
const RecipeDetailModal = ({ recipe, onClose, onToShopping, onCopyRecipe, t, isDark }) => {
  const prepTime = recipe.prep_time || recipe.prepTime || 0;
  const cookTime = recipe.cook_time || recipe.cookTime || 0;
  const totalTime = prepTime + cookTime;
  const isCommunity = recipe.isCommunity;
  
  const imageUrl = recipe.image_url || recipe.imageUrl || '';
  const imageKey = recipe.image_key || '';
  const isRealImage = imageUrl.startsWith('http') || imageUrl.startsWith('/');
  const emoji = getRecipeEmoji(imageKey || imageUrl);
  
  const modalStyles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
    modal: { background: '#FFF', borderRadius: '20px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', border: isCommunity ? '2px solid #93C5FD' : `2px solid ${PLANIVIO.border}` },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem', borderBottom: isCommunity ? '2px solid #93C5FD' : `2px solid ${PLANIVIO.border}`, background: isCommunity ? '#EFF6FF' : PLANIVIO.bgLight },
    body: { padding: '1.25rem' },
    /* 🔧 FIX 7: Modal-Footer mit align-items:center für L1+L2 Ausnahme */
    footer: { display: 'flex', gap: BTN.gap, padding: '1.25rem', borderTop: isCommunity ? '2px solid #93C5FD' : `2px solid ${PLANIVIO.border}`, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' },
    emojiHeader: { 
      width: '100%', 
      height: '180px', 
      background: PLANIVIO.gradient, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      fontSize: '5rem',
      borderRadius: '12px',
      marginBottom: '1rem',
      overflow: 'hidden',
      position: 'relative'
    },
    /* 🔧 FIX 10: Modal Close → L4 Mini (32px visuell + 44px touch) */
    closeBtn: {
      width: BTN.iconMiniSize,
      height: BTN.iconMiniSize,
      background: 'transparent',
      border: 'none',
      borderRadius: BTN.iconMiniR,
      fontSize: '1rem',
      color: PLANIVIO.textMuted,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      padding: 0,
      lineHeight: 1,
      boxSizing: 'border-box',
      flexShrink: 0,
      transition: 'all 0.2s ease'
    },
    /* 🔧 FIX 7: Primary Footer Button → L1 (52px, 14px, gradient+shadow) */
    footerPrimary: {
      height: BTN.primaryH,
      padding: BTN.primaryPad,
      background: PLANIVIO.gradient,
      color: 'white',
      border: 'none',
      borderRadius: BTN.primaryR,
      fontSize: BTN.primaryFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      boxShadow: BTN.primaryShadow,
      transition: 'all 0.2s ease',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap'
    },
    /* 🔧 FIX 7: Secondary Footer Button → L2 (44px, 12px, teal border) */
    footerSecondary: {
      height: BTN.secondaryH,
      padding: BTN.secondaryPad,
      background: 'white',
      color: PLANIVIO.teal,
      border: `2px solid ${PLANIVIO.teal}`,
      borderRadius: BTN.secondaryR,
      fontSize: BTN.secondaryFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap'
    }
  };
  
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <div>
            <h2 style={{ margin: 0, color: PLANIVIO.text }}>{recipe.name}</h2>
            {isCommunity && <span style={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: 600 }}>🌍 Community Rezept</span>}
          </div>
          {/* 🔧 FIX 10: Close → L4 Mini */}
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={modalStyles.body}>
          <div style={modalStyles.emojiHeader}>
            {isRealImage ? (
              <img src={imageUrl} alt={recipe.name} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }} />
            ) : (
              emoji
            )}
          </div>
          
          <p style={{ color: PLANIVIO.textMuted, marginBottom: '1rem' }}>{recipe.description}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <span style={{ padding: '0.375rem 0.875rem', background: PLANIVIO.bgLight, border: `1.5px solid ${PLANIVIO.border}`, borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>📁 {recipe.category}</span>
            <span style={{ padding: '0.375rem 0.875rem', background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>👥 {recipe.servings} Portionen</span>
            <span style={{ padding: '0.375rem 0.875rem', background: PLANIVIO.bgLight, border: `1.5px solid ${PLANIVIO.border}`, borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600 }}>⏱️ {totalTime} Min</span>
          </div>
          <h3 style={{ color: PLANIVIO.text, marginBottom: '0.75rem' }}>📋 Zutaten</h3>
          <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', color: PLANIVIO.text }}>
            {recipe.ingredients?.map((ing, i) => <li key={i} style={{ marginBottom: '0.25rem' }}><strong style={{ color: PLANIVIO.teal }}>{ing.quantity}</strong> {ing.unit} {ing.name}</li>)}
          </ul>
          <h3 style={{ color: PLANIVIO.text, marginBottom: '0.75rem' }}>👨‍🍳 Zubereitung</h3>
          <ol style={{ paddingLeft: '1.5rem', color: PLANIVIO.text }}>
            {recipe.instructions?.map((inst, i) => <li key={i} style={{ marginBottom: '0.5rem' }}>{typeof inst === 'string' ? inst : inst.instruction || inst}</li>)}
          </ol>
        </div>
        {/* 🔧 FIX 7: Modal-Footer mit Strategy-konformen Buttons */}
        <div style={modalStyles.footer}>
          <button 
            onClick={onClose} 
            style={modalStyles.footerSecondary}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
          >
            Schließen
          </button>
          <button 
            onClick={(e) => { onToShopping(recipe, e); onClose(); }} 
            style={modalStyles.footerPrimary}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(74,144,164,0.35)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = BTN.primaryShadow; }}
          >
            🛒 Zur Einkaufsliste
          </button>
          {isCommunity && (
            <button 
              onClick={(e) => { onCopyRecipe(recipe, e); onClose(); }} 
              style={modalStyles.footerSecondary}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              📋 Kopieren
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STYLES — 🔧 v25.5: BUTTON STRATEGY COMPLIANCE
// ============================================================================
function getStyles(isDark, isMobile = false) {
  return {
    container: { padding: isMobile ? '5px' : '2rem 1.25rem', maxWidth: isMobile ? '100%' : '1400px', margin: '0 auto', width: '100%', background: '#E8F4F8', minHeight: 'calc(100vh - 100px)', borderRadius: isMobile ? '0' : '20px', boxSizing: 'border-box', overflowX: 'hidden' },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1 },
    headerIcon: { width: '48px', height: '48px', background: PLANIVIO.gradient, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', boxShadow: `0 4px 12px ${PLANIVIO.shadow}` },
    headerTitle: { margin: 0, color: PLANIVIO.text, fontSize: '1.5rem', fontWeight: 700 },
    headerSubtitle: { margin: 0, color: PLANIVIO.textMuted, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '8px' },
    
    // KEPT: infoBtn bleibt unverändert per User-Anweisung
    infoBtn: { width: isMobile ? '32px' : '44px', height: isMobile ? '32px' : '44px', background: isMobile ? 'transparent' : '#F8FAFC', border: isMobile ? 'none' : '1px solid #E2E8F0', borderRadius: '12px', fontSize: isMobile ? '1rem' : '1.25rem', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', marginLeft: isMobile ? '0.25rem' : '0.5rem', flexShrink: 0, boxSizing: 'border-box', position: 'relative', padding: 0, lineHeight: 1 },
    infoModal: { background: 'white', border: `2px solid ${PLANIVIO.border}`, borderRadius: '16px', marginBottom: '1rem', boxShadow: `0 4px 20px ${PLANIVIO.shadow}`, overflow: 'hidden' },
    infoModalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: PLANIVIO.gradient, color: 'white' },
    infoModalTitle: { fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    infoCloseBtn: { background: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', fontSize: '1rem', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' },
    infoModalContent: { padding: '1.25rem' },
    infoText: { margin: '0 0 1rem 0', color: '#4B5563', fontSize: '0.9rem', lineHeight: 1.6 },
    infoList: { margin: 0, padding: '0 0 0 1.25rem', color: '#4B5563', fontSize: '0.85rem', lineHeight: 1.8 },

    // 🔧 FIX 5+6: viewToggle → 36px Chip, pill, overflow:hidden, no inner radius
    viewToggle: { 
      display: 'inline-flex', 
      gap: 0, 
      background: '#FFF', 
      border: '1px solid #E2E8F0', 
      padding: 0, 
      borderRadius: BTN.chipR, 
      height: BTN.chipH, 
      width: 'fit-content', 
      boxSizing: 'border-box',
      overflow: 'hidden'
    },
    toggleBtn: { 
      padding: '0 16px', 
      height: '100%', 
      border: 'none', 
      borderRadius: 0, /* Keine innere Rundung — Container macht das */
      cursor: 'pointer', 
      fontSize: BTN.chipFs, 
      fontWeight: 500, 
      transition: 'all 0.2s', 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap'
    },
    toggleBtnActive: { background: PLANIVIO.gradient, color: 'white', fontWeight: 600 },
    toggleBtnInactive: { background: 'transparent', color: '#475569' },

    // FILTERS
    filtersContainer: { 
      background: '#FFF', 
      border: `2px solid ${PLANIVIO.border}`, 
      borderRadius: '16px', 
      padding: '0.75rem 1rem', 
      marginBottom: '1.5rem', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      flexWrap: 'nowrap',
      width: '100%',
      boxSizing: 'border-box'
    },
    searchInput: { 
      flex: '1 1 auto',
      minWidth: '100px', 
      padding: '0.5rem 0.75rem', 
      border: `2px solid ${PLANIVIO.border}`, 
      borderRadius: '10px', 
      fontSize: '0.85rem', 
      outline: 'none', 
      background: PLANIVIO.bgLight, 
      color: PLANIVIO.text,
      boxSizing: 'border-box'
    },
    selectInput: { 
      flex: '0 1 auto',
      padding: '0.5rem 0.5rem', 
      border: `2px solid ${PLANIVIO.border}`, 
      borderRadius: '10px', 
      fontSize: '0.8rem', 
      outline: 'none', 
      background: PLANIVIO.bgLight, 
      color: PLANIVIO.text, 
      cursor: 'pointer',
      boxSizing: 'border-box'
    },

    // ERROR
    errorContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', background: '#FEF2F2', borderRadius: '16px', border: '2px solid #FECACA', gap: '1rem', color: '#991B1B' },
    errorBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: '#FEF2F2', border: '2px solid #FECACA', borderRadius: '12px', marginBottom: '1rem', color: '#991B1B' },
    errorCloseBtn: { background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#991B1B' },
    
    // 🔧 FIX 9: retryBtn → L1 Primary (52px, 14px, gradient+shadow)
    retryBtn: { 
      height: BTN.primaryH, 
      padding: BTN.primaryPad, 
      background: PLANIVIO.gradient, 
      color: 'white', 
      border: 'none', 
      borderRadius: BTN.primaryR, 
      fontSize: BTN.primaryFs, 
      fontWeight: BTN.fw, 
      cursor: 'pointer', 
      boxShadow: BTN.primaryShadow,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.2s ease'
    },

    // GRID
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' },
    card: { background: '#FFF', border: `2px solid ${PLANIVIO.border}`, borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s' },
    cardImage: { width: '100%', height: '180px', background: PLANIVIO.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' },
    cardEmoji: { fontSize: '5rem', opacity: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' },
    categoryBadge: { position: 'absolute', top: '12px', right: '12px', padding: '0.375rem 0.875rem', background: 'rgba(255,255,255,0.95)', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700, color: PLANIVIO.teal, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
    communityBadge: { position: 'absolute', top: '12px', left: '12px', padding: '0.25rem 0.625rem', background: 'rgba(59,130,246,0.95)', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, color: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
    cardContent: { padding: '1.25rem' },
    cardTitle: { margin: '0 0 0.5rem 0', fontSize: '1.15rem', fontWeight: 700, color: PLANIVIO.text },
    cardDescription: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: PLANIVIO.textMuted, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    cardMeta: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' },
    metaBadge: { padding: '0.375rem 0.625rem', background: '#FFF', border: `1.5px solid ${PLANIVIO.border}`, borderRadius: '8px', fontSize: '0.8rem', color: PLANIVIO.text, fontWeight: 500 },
    difficultyBadge: { display: 'inline-block', padding: '0.375rem 0.75rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600 },
    cardActions: { display: 'flex', gap: BTN.gapCompact, marginTop: '1rem', paddingTop: '1rem', borderTop: `1.5px solid ${PLANIVIO.borderLight}` },

    // 🔧 FIX 2: shopBtn → L4 Icon-Button (KEIN Gradient!)
    shopBtn: { 
      flex: 1, 
      height: BTN.iconSize, 
      padding: 0, 
      background: BTN.iconBg, 
      border: BTN.iconBorder,
      borderRadius: BTN.iconR, 
      cursor: 'pointer', 
      fontSize: '1.1rem', 
      color: BTN.iconColor, 
      transition: 'all 0.15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },
    // 🔧 FIX 4: deleteBtn → L4 Icon-Button mit Danger-Hover
    deleteBtn: { 
      flex: 1, 
      height: BTN.iconSize, 
      padding: 0, 
      background: BTN.iconBg, 
      border: BTN.iconBorder,
      borderRadius: BTN.iconR, 
      cursor: 'pointer', 
      fontSize: '1.1rem', 
      color: PLANIVIO.textMuted,
      transition: 'all 0.15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },
    // 🔧 FIX 3: copyBtn → L4 Icon-Button (KEIN Blau-Gradient!)
    copyBtn: { 
      flex: 1, 
      height: BTN.iconSize, 
      padding: 0, 
      background: BTN.iconBg, 
      border: BTN.iconBorder,
      borderRadius: BTN.iconR, 
      cursor: 'pointer', 
      fontSize: '1.1rem', 
      color: BTN.iconColor,
      transition: 'all 0.15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },

    // 🔧 FIX 1: FAB — L5 (56px, 16px radius, fixed, gradient)
    fab: {
      position: 'fixed',
      bottom: '72px', /* Strategie: 72px über Bottom-Nav */
      right: '16px',
      zIndex: 9999,
      width: BTN.fabSize,
      height: BTN.fabSize,
      padding: 0,
      background: PLANIVIO.gradient,
      color: 'white',
      border: 'none',
      borderRadius: BTN.fabR,
      fontSize: '1.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      boxShadow: BTN.fabShadow,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      touchAction: 'none',
      userSelect: 'none',
      transition: 'all 0.2s ease'
    },

    // LIST VIEW — 🔧 FIX 8: List-Buttons als L4 Icon-Buttons
    listContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      background: '#FFF',
      border: `2px solid ${PLANIVIO.border}`,
      borderRadius: '16px',
      padding: '0.5rem',
      overflow: 'hidden'
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      background: PLANIVIO.bgLight,
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      borderLeft: `4px solid ${PLANIVIO.teal}`,
      marginBottom: '0.5rem'
    },
    listImage: {
      width: '50px',
      height: '50px',
      borderRadius: '8px',
      background: PLANIVIO.gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      flexShrink: 0
    },
    listContent: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    listTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: PLANIVIO.text,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    listCommunityBadge: { padding: '0.1rem 0.375rem', background: '#3B82F6', color: 'white', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700 },
    listMeta: {
      fontSize: '0.75rem',
      color: PLANIVIO.textMuted,
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    listBadges: { display: 'flex', gap: '0.375rem', flexShrink: 0, alignItems: 'center', marginLeft: 'auto' },
    listCategoryBadge: { padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, background: '#FFF', color: PLANIVIO.teal, border: `1px solid ${PLANIVIO.border}` },
    listDiffBadge: { padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 },
    // 🔧 FIX 8: List-Buttons → L4 Icon (44px, 12px, #F8FAFC)
    listShopBtn: { 
      width: BTN.iconSize, 
      height: BTN.iconSize, 
      padding: 0, 
      background: BTN.iconBg, 
      border: BTN.iconBorder,
      borderRadius: BTN.iconR, 
      cursor: 'pointer', 
      fontSize: '1rem',
      color: BTN.iconColor,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.15s ease'
    },
    listDeleteBtn: { 
      width: BTN.iconSize, 
      height: BTN.iconSize, 
      padding: 0, 
      background: BTN.iconBg, 
      border: BTN.iconBorder,
      borderRadius: BTN.iconR, 
      cursor: 'pointer', 
      fontSize: '1rem', 
      color: PLANIVIO.textMuted,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.15s ease'
    },
    listCopyBtn: { 
      width: BTN.iconSize, 
      height: BTN.iconSize, 
      padding: 0, 
      background: BTN.iconBg, 
      border: BTN.iconBorder,
      borderRadius: BTN.iconR, 
      cursor: 'pointer', 
      fontSize: '1rem', 
      color: BTN.iconColor,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.15s ease'
    }
  };
}

export default Recipes;